import type { TypeId } from "./types.ts";
import { FRONTIER_MOVES } from "./movesFrontier.ts";
import { UNDERNET_MOVES } from "./movesUndernet.ts";
import { SIDECHAIN_MOVES } from "./movesSidechain.ts";
import { TOOLKIT_MOVES } from "./movesToolkit.ts";
import { STATUS_META, type StatusId } from "./status.ts";

export type MoveKind = "strike" | "hex" | "guard";

export type MoveFx =
  | { kind: "stat"; stat: "atk" | "def" | "spd"; stages: number; target: "foe" | "self" }
  | { kind: "heal"; pct: number }
  | { kind: "drain"; pct: number }
  | { kind: "recoil"; pct: number }
  | { kind: "flinch"; chance: number }
  | { kind: "surehit" }
  /** Strikes min..max times in one turn; each hit rolls its own spread, crit rolls once. */
  | { kind: "multi"; min: number; max: number }
  /** Inflicts a battle condition: always for status moves, on a chance roll after a damaging hit. */
  | { kind: "status"; status: StatusId; chance: number }
  /** Raises two of the user's own stats in one turn. */
  | { kind: "stats"; atk?: number; def?: number; spd?: number }
  /** Shakes off the user's condition, then restores a share of max HP. */
  | { kind: "cure"; pct: number };

/**
 * How an attack looks. Purely visual: timing still comes from the family (melee, projectile, beam,
 * status), and a move without a style gets one derived from its family and effect.
 */
export type AttackStyle =
  | "strike" | "slash" | "slam" | "flurry"
  | "orb" | "volley" | "rain" | "siphon"
  | "beam" | "wave" | "vortex"
  | "hex" | "boost" | "shield" | "heal";

export interface MoveDef {
  id: string;
  name: string;
  type: TypeId;
  power: number;
  acc: number;
  pp: number;
  kind: MoveKind;
  crit: number;
  fx?: MoveFx;
  /** Moves with higher priority act before speed is compared (default 0). */
  priority?: number;
  /** Animation family override for moves outside the authored beam/projectile lists. */
  anim?: "melee" | "beam" | "projectile";
  /** Visual style; also picks the family for damaging moves that set no `anim`. */
  style?: AttackStyle;
  lore: string;
}

export const MOVES: Record<string, MoveDef> = {
  ribbitRug: {
    id: "ribbitRug",
    name: "Ribbit Rug",
    type: "chaos",
    power: 55,
    acc: 95,
    pp: 20,
    kind: "strike",
    crit: 0,
    lore: "Yanks the lily pad. The contract was the pad.",
  },
  pondPump: {
    id: "pondPump",
    name: "Pond Pump",
    type: "liquid",
    power: 40,
    acc: 100,
    pp: 25,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Floods the lane. Feet stay. That is the fee. The lane bills by the inch.",
  },
  stickyHands: {
    id: "stickyHands",
    name: "Sticky Hands",
    type: "liquid",
    power: 0,
    acc: 100,
    pp: 20,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "A damp handshake. He still has your Tuesday.",
  },
  copeSplash: {
    id: "copeSplash",
    name: "Cope Splash",
    type: "cope",
    power: 70,
    acc: 90,
    pp: 10,
    kind: "strike",
    crit: 0,
    lore: "Tears, pondwater, same splash. Nobody asks which.",
  },
  bonk: {
    id: "bonk",
    name: "Bonk",
    type: "flex",
    power: 50,
    acc: 100,
    pp: 25,
    kind: "strike",
    crit: 1,
    lore: "A clean bat to the timeline. The joke walks again.",
  },
  suchSpeed: {
    id: "suchSpeed",
    name: "Such Speed",
    type: "viral",
    power: 0,
    acc: 100,
    pp: 20,
    kind: "guard",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 2, target: "self" },
    lore: "Wow. Very go. Much first. The street files later.",
  },
  rareTreat: {
    id: "rareTreat",
    name: "Rare Treat",
    type: "viral",
    power: 0,
    acc: 100,
    pp: 10,
    kind: "guard",
    crit: 0,
    fx: { kind: "heal", pct: 0.4 },
    lore: "So rare the HP believes the rumor. The rumor files first.",
  },
  moonHowl: {
    id: "moonHowl",
    name: "Moon Howl",
    type: "signal",
    power: 80,
    acc: 85,
    pp: 8,
    kind: "strike",
    crit: 0,
    lore: "Calls a late candle across the pond. It comes anyway. Clock out later.",
  },
  feelsBad: {
    id: "feelsBad",
    name: "Feels Bad",
    type: "cope",
    power: 40,
    acc: 100,
    pp: 25,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "A sigh with a hitbox. Empathy, itemized. Invoice to follow.",
  },
  panicSell: {
    id: "panicSell",
    name: "Panic Sell",
    type: "chaos",
    power: 75,
    acc: 90,
    pp: 10,
    kind: "strike",
    crit: 0,
    fx: { kind: "recoil", pct: 0.15 },
    lore: "Dumps the bag and the face it was wearing. Both were rented.",
  },
  hopium: {
    id: "hopium",
    name: "Hopium",
    type: "cope",
    power: 0,
    acc: 100,
    pp: 10,
    kind: "guard",
    crit: 0,
    fx: { kind: "heal", pct: 0.5 },
    lore: "A deep inhale of maybe-tomorrow. Exhale optional.",
  },
  existentialStare: {
    id: "existentialStare",
    name: "Existential Stare",
    type: "signal",
    power: 0,
    acc: 90,
    pp: 15,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "def", stages: -2, target: "foe" },
    lore: "Makes them read the comments. No one recovers.",
  },
  jawlineFlex: {
    id: "jawlineFlex",
    name: "Jawline Flex",
    type: "flex",
    power: 55,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "The chin files the minutes. Nobody objects.",
  },
  absoluteCinema: {
    id: "absoluteCinema",
    name: "Absolute Cinema",
    type: "flex",
    power: 84,
    acc: 80,
    pp: 8,
    kind: "strike",
    crit: 1,
    lore: "A still so clean it bills you for the silence. Applause is extra.",
  },
  silentNod: {
    id: "silentNod",
    name: "Silent Nod",
    type: "signal",
    power: 0,
    acc: 100,
    pp: 20,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "A nod so quiet it rattles the fillings. The street heard it.",
  },
  grindset: {
    id: "grindset",
    name: "Grindset",
    type: "flex",
    power: 0,
    acc: 100,
    pp: 15,
    kind: "guard",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 2, target: "self" },
    lore: "4am. No excuses. Pixel sweat. The tuque stays on.",
  },
  laserGlare: {
    id: "laserGlare",
    name: "Laser Glare",
    type: "signal",
    power: 65,
    acc: 95,
    pp: 15,
    kind: "strike",
    crit: 1,
    lore: "Red diamonds, no blink. The bag was never thrown.",
  },
  diamondBark: {
    id: "diamondBark",
    name: "Diamond Bark",
    type: "flex",
    power: 50,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    lore: "Hands so diamond they echo. The room was already empty.",
  },
  airdropFetch: {
    id: "airdropFetch",
    name: "Airdrop Fetch",
    type: "viral",
    power: 35,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    fx: { kind: "drain", pct: 0.5 },
    lore: "Snatches value mid-air. Still no thrower. Still no refund.",
  },
  trendChase: {
    id: "trendChase",
    name: "Trend Chase",
    type: "viral",
    power: 70,
    acc: 90,
    pp: 10,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 1, target: "self" },
    lore: "Already at the next joke. This one can file a late slip.",
  },
  marketCapsize: {
    id: "marketCapsize",
    name: "Market Capsize",
    type: "liquid",
    power: 88,
    acc: 80,
    pp: 5,
    kind: "strike",
    crit: 0,
    lore: "Rolls the pond. The dip becomes the weather. Everyone swims in the forecast.",
  },
  deepBuy: {
    id: "deepBuy",
    name: "Deep Buy",
    type: "liquid",
    power: 0,
    acc: 100,
    pp: 10,
    kind: "guard",
    crit: 0,
    fx: { kind: "heal", pct: 0.45 },
    lore: "Absorbs the dip like a lungful. Holds it. The weather notices.",
  },
  breach: {
    id: "breach",
    name: "Breach",
    type: "flex",
    power: 60,
    acc: 100,
    pp: 15,
    kind: "strike",
    crit: 0,
    fx: { kind: "flinch", chance: 0.2 },
    lore: "Surfaces hard. The room forgets its next word.",
  },
  liquidityWave: {
    id: "liquidityWave",
    name: "Liquidity Wave",
    type: "liquid",
    power: 45,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "A wall of cash with a crest. Bring goggles. The receipt floats face up.",
  },
  bananaHands: {
    id: "bananaHands",
    name: "Banana Hands",
    type: "viral",
    power: 50,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    lore: "Peel first. The question never had a chance. Neither did the peel.",
  },
  floorTogether: {
    id: "floorTogether",
    name: "Floor Together",
    type: "cope",
    power: 0,
    acc: 100,
    pp: 15,
    kind: "guard",
    crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "We sit. We hold. The floor is a group project.",
  },
  chestBeat: {
    id: "chestBeat",
    name: "Chest Beat",
    type: "flex",
    power: 65,
    acc: 95,
    pp: 15,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Percussion as policy. Minutes taken in bruises.",
  },
  fomoSwing: {
    id: "fomoSwing",
    name: "FOMO Swing",
    type: "chaos",
    power: 85,
    acc: 85,
    pp: 8,
    kind: "strike",
    crit: 1,
    fx: { kind: "recoil", pct: 0.1 },
    lore: "Late, loud, and almost worth the bruise. The queue claps for the wrong part.",
  },
  pullTheCord: {
    id: "pullTheCord",
    name: "Pull the Cord",
    type: "chaos",
    power: 80,
    acc: 90,
    pp: 8,
    kind: "strike",
    crit: 0,
    lore: "The rug was the floor. The floor was a courtesy.",
  },
  fakeChart: {
    id: "fakeChart",
    name: "Fake Chart",
    type: "signal",
    power: 0,
    acc: 95,
    pp: 15,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -2, target: "foe" },
    lore: "A green candle drawn in marker. It still prints.",
  },
  smokeExit: {
    id: "smokeExit",
    name: "Smoke Exit",
    type: "chaos",
    power: 40,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 1, target: "self" },
    lore: "Gone before the thread loads. Leave a rating.",
  },
  trustMeBro: {
    id: "trustMeBro",
    name: "Trust Me Bro",
    type: "signal",
    power: 0,
    acc: 100,
    pp: 10,
    kind: "hex",
    crit: 0,
    fx: { kind: "surehit" },
    lore: "Next hit cannot miss. He said bro. That is the bit.",
  },
  liftoff: {
    id: "liftoff",
    name: "Liftoff",
    type: "signal",
    power: 70,
    acc: 90,
    pp: 10,
    kind: "strike",
    crit: 0,
    lore: "Leaves gravity and doubt on read. The crater files the flight plan after.",
  },
  zeroGravity: {
    id: "zeroGravity",
    name: "Zero Gravity",
    type: "signal",
    power: 0,
    acc: 100,
    pp: 15,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -2, target: "foe" },
    lore: "Their feet forget the ground. The invoice does not.",
  },
  craterStomp: {
    id: "craterStomp",
    name: "Crater Stomp",
    type: "flex",
    power: 85,
    acc: 90,
    pp: 8,
    kind: "strike",
    crit: 0,
    lore: "Lands like a headline. No correction issued.",
  },
  orbitTaunt: {
    id: "orbitTaunt",
    name: "Orbit Taunt",
    type: "viral",
    power: 0,
    acc: 100,
    pp: 20,
    kind: "hex",
    crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Circles them until they swing wild. The orbit takes attendance.",
  },
  cashRain: {
    id: "cashRain",
    name: "Cash Rain",
    type: "liquid",
    power: 55,
    acc: 100,
    pp: 15,
    kind: "strike",
    crit: 0,
    fx: { kind: "drain", pct: 0.3 },
    lore: "Bills become weather. Bring a lid. And a lawyer.",
  },
  hoverTax: {
    id: "hoverTax",
    name: "Hover Tax",
    type: "viral",
    power: 45,
    acc: 100,
    pp: 20,
    kind: "strike",
    crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "A fee for standing still. Sitting is a separate invoice.",
  },
  rotorSlap: {
    id: "rotorSlap",
    name: "Rotor Slap",
    type: "flex",
    power: 70,
    acc: 95,
    pp: 10,
    kind: "strike",
    crit: 0,
    lore: "Blades first. The question never made the agenda.",
  },
  bailout: {
    id: "bailout",
    name: "Bailout",
    type: "liquid",
    power: 0,
    acc: 100,
    pp: 5,
    kind: "guard",
    crit: 0,
    fx: { kind: "heal", pct: 0.55 },
    lore: "Emergency funds, rotary edition. The rescue is also a fee.",
  },
  ringFlash: {
    id: "ringFlash", name: "Ring Flash", type: "signal", power: 45, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A perfect circle of light. Even the reeds look retouched.",
  },
  wingClip: {
    id: "wingClip", name: "Wing Clip", type: "viral", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 1,
    lore: "A sharp turn through a crowded timeline. No one yielded.",
  },
  hypeLoop: {
    id: "hypeLoop", name: "Hype Loop", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "spd", "stages": 1, "target": "self"},
    lore: "One more lap. The ring light does not get tired.",
  },
  confettiBurst: {
    id: "confettiBurst", name: "Confetti Burst", type: "flex", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "A stage finale that fits in a pocket. Barely.",
  },
  greenWick: {
    id: "greenWick", name: "Green Wick", type: "cope", power: 45, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A maybe-ember that still reaches the back row. Nobody sat there. It reached anyway.",
  },
  steadyShell: {
    id: "steadyShell", name: "Steady Shell", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "def", "stages": 2, "target": "self"},
    lore: "The wax hardens. The little flame stays lit.",
  },
  candleSip: {
    id: "candleSip", name: "Candle Sip", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: {"kind": "heal", "pct": 0.4},
    lore: "One sip. The wick pretends it slept. The night files the difference.",
  },
  copeCrash: {
    id: "copeCrash", name: "Cope Crash", type: "chaos", power: 70, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    fx: {"kind": "recoil", "pct": 0.1},
    lore: "Optimism with a helmet. The helmet files first.",
  },
  wickWhip: {
    id: "wickWhip", name: "Wick Whip", type: "chaos", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The wick learned range. The lane learned manners the hard way.",
  },
  chartFlare: {
    id: "chartFlare", name: "Chart Flare", type: "chaos", power: 65, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The candlestick stands up. Everyone else sits.",
  },
  trendTilt: {
    id: "trendTilt", name: "Trend Tilt", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: {"kind": "stat", "stat": "atk", "stages": -1, "target": "foe"},
    lore: "Turns the chart sideways until the threat looks small. The threat did not shrink. The axis did.",
  },
  candleBreath: {
    id: "candleBreath", name: "Candle Breath", type: "flex", power: 80, acc: 85, pp: 8,
    kind: "strike", crit: 0,
    lore: "Patient wick, impatient room. The breath wins.",
  },
  paperCut: {
    id: "paperCut", name: "Paper Cut", type: "liquid", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "The crease remembers your finger. You do too.",
  },
  bagBrine: {
    id: "bagBrine", name: "Bag Brine", type: "liquid", power: 60, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind": "stat", "stat": "spd", "stages": -1, "target": "foe"},
    lore: "Pondwater in the boots. The lease did not mention that.",
  },
  foldGuard: {
    id: "foldGuard", name: "Fold Guard", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "def", "stages": 2, "target": "self"},
    lore: "A careful crease becomes a load-bearing wall. The shell files the permit after.",
  },
  diamondPress: {
    id: "diamondPress", name: "Diamond Press", type: "flex", power: 75, acc: 95, pp: 10,
    kind: "strike", crit: 0,
    lore: "Two palms. One lane. The floor files the minutes.",
  },
  rushPeck: {
    id: "rushPeck", name: "Rush Peck", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Peck first, ask why from the front of the line. The line answers last.",
  },
  queueCry: {
    id: "queueCry", name: "Queue Cry", type: "cope", power: 55, acc: 95, pp: 20,
    kind: "strike", crit: 0,
    fx: {"kind": "stat", "stat": "atk", "stages": -1, "target": "foe"},
    lore: "One chirp. The whole line forgets why it was waiting.",
  },
  refresh: {
    id: "refresh", name: "Refresh", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "spd", "stages": 2, "target": "self"},
    lore: "Checks the empty branch. Refresh is a personality.",
  },
  herdRush: {
    id: "herdRush", name: "Herd Rush", type: "chaos", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    fx: {"kind": "recoil", "pct": 0.15},
    lore: "Everyone runs. The destination filed a missing-person report.",
  },
  feeBite: {
    id: "feeBite", name: "Fee Bite", type: "chaos", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    fx: {"kind": "drain", "pct": 0.35},
    lore: "A small bite out of the balance. The smoke has its own line.",
  },
  sootSpit: {
    id: "sootSpit", name: "Soot Spit", type: "chaos", power: 65, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "A chimney cough. The bill arrives before the smoke clears.",
  },
  meterUp: {
    id: "meterUp", name: "Meter Up", type: "chaos", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "atk", "stages": 2, "target": "self"},
    lore: "The brass meter spins faster. That is not encouragement.",
  },
  ventBeam: {
    id: "ventBeam", name: "Vent Beam", type: "chaos", power: 80, acc: 85, pp: 8,
    kind: "strike", crit: 0,
    lore: "Furnace glare through a crooked vent. The meter approves.",
  },
  spreadSnap: {
    id: "spreadSnap", name: "Spread Snap", type: "liquid", power: 50, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Snaps the gap between two puddles. The quote misses both.",
  },
  slipstream: {
    id: "slipstream", name: "Slipstream", type: "liquid", power: 65, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind": "stat", "stat": "spd", "stages": 1, "target": "self"},
    lore: "Rides the leftover current. The quote never quite lands.",
  },
  thinPool: {
    id: "thinPool", name: "Thin Pool", type: "liquid", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: {"kind": "stat", "stat": "def", "stages": -2, "target": "foe"},
    lore: "The shallow end loses its cover. Bring your own floor.",
  },
  brineSiphon: {
    id: "brineSiphon", name: "Brine Siphon", type: "liquid", power: 55, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind": "drain", "pct": 0.5},
    lore: "Sips the lane. Sends the salt back as a refund.",
  },
  ticketToss: {
    id: "ticketToss", name: "Ticket Toss", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A leaf ticket, edge first. Admit one. No refunds.",
  },
  mintRay: {
    id: "mintRay", name: "Mint Ray", type: "viral", power: 65, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "Green light through a punched wing. Limited, it insists.",
  },
  capeGuard: {
    id: "capeGuard", name: "Cape Guard", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "def", "stages": 2, "target": "self"},
    lore: "Folds the cape into a quilt. The grant can wait.",
  },
  claimDrift: {
    id: "claimDrift", name: "Claim Drift", type: "flex", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Lands with the full weight of a coupon. The nest is empty.",
  },
  snowStake: {
    id: "snowStake", name: "Snow Stake", type: "flex", power: 65, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    lore: "One snowshoe, one pile. The stake is the sitting.",
  },
  thawDrip: {
    id: "thawDrip", name: "Thaw Drip", type: "liquid", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    fx: {"kind": "stat", "stat": "spd", "stages": -1, "target": "foe"},
    lore: "Cold runoff. Hurried feet file a delay. The puddle keeps the schedule.",
  },
  warmReserve: {
    id: "warmReserve", name: "Warm Reserve", type: "liquid", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: {"kind": "heal", "pct": 0.4},
    lore: "The thermos was in the coat the whole time. Sit.",
  },
  driftWall: {
    id: "driftWall", name: "Drift Wall", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: {"kind": "stat", "stat": "def", "stages": 2, "target": "self"},
    lore: "Snow, stacked. The wall has a waiting list. It never moves the line.",
  },
  crossBaton: {
    id: "crossBaton", name: "Cross Baton", type: "flex", power: 75, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"flinch","chance":0.15},
    lore: "Crossed batons on cobbles. The joke remembers the time.",
  },
  nightSignal: {
    id: "nightSignal", name: "Night Signal", type: "viral", power: 80, acc: 95, pp: 10,
    kind: "strike", crit: 0,
    lore: "Late patrol, cone of lamplight. Clock out is a rumor.",
  },
  reedDecree: {
    id: "reedDecree", name: "Reed Decree", type: "liquid", power: 70, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"spd","stages":-1,"target":"foe"},
    lore: "Closes the sluice behind hurried feet. The pond keeps the tip.",
  },
  marshDue: {
    id: "marshDue", name: "Marsh Due", type: "cope", power: 65, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"drain","pct":0.4},
    lore: "Collects courage from the flooded lane. Interest applies.",
  },
  bottleStorm: {
    id: "bottleStorm", name: "Bottle Storm", type: "cope", power: 75, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "Uncorks a season of saved rain. Still refreshing. Still charging.",
  },
  lastNerve: {
    id: "lastNerve", name: "Last Nerve", type: "chaos", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 1,
    lore: "The rain pilgrim puts a foot down. The tuque does not come off.",
  },
  prismFang: {
    id: "prismFang", name: "Prism Fang", type: "signal", power: 80, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "The collar splits the light. The bag was still never thrown.",
  },
  echoFetch: {
    id: "echoFetch", name: "Echo Fetch", type: "viral", power: 65, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"spd","stages":1,"target":"self"},
    lore: "Catches the echo before it lands. Still no thrower.",
  },
  cordSever: {
    id: "cordSever", name: "Cord Sever", type: "chaos", power: 80, acc: 95, pp: 15,
    kind: "strike", crit: 1,
    lore: "Three tail prongs cut one very bad promise. The outlet kept the prongs.",
  },
  falseBeacon: {
    id: "falseBeacon", name: "False Beacon", type: "signal", power: 65, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"def","stages":-1,"target":"foe"},
    lore: "A woven decoy. The guard follows the wrong cord.",
  },
  canopyCrash: {
    id: "canopyCrash", name: "Canopy Crash", type: "viral", power: 80, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "Drops from a branch with both palms. No exit interview.",
  },
  drumRoll: {
    id: "drumRoll", name: "Drum Roll", type: "chaos", power: 70, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"atk","stages":1,"target":"self"},
    lore: "The canopy keeps time. The drum does the policy.",
  },
  orbitLance: {
    id: "orbitLance", name: "Orbit Lance", type: "signal", power: 85, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "A straight line through a polite orbit. The flag ducks.",
  },
  flagfall: {
    id: "flagfall", name: "Flagfall", type: "flex", power: 75, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"def","stages":-1,"target":"foe"},
    lore: "Plants a crater flag where the guard was standing. Orbit: polite.",
  },
  twinRotor: {
    id: "twinRotor", name: "Twin Rotor", type: "viral", power: 80, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "Two rotors, one receipt. Weather is still a line item.",
  },
  rainFreight: {
    id: "rainFreight", name: "Rain Freight", type: "liquid", power: 70, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: {"kind":"stat","stat":"atk","stages":-1,"target":"foe"},
    lore: "A weather parcel. The swing files a delay. The invoice does not.",
  },
  waxLance: {
    id: "waxLance", name: "Wax Lance", type: "flex", power: 75, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    lore: "The wick learned a spear. The rumor got a point.",
  },
  canopyFlare: {
    id: "canopyFlare", name: "Canopy Flare", type: "chaos", power: 85, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    fx: {"kind":"recoil","pct":0.1},
    lore: "Lights the canopy. Singes its own wick. Still calls it a plan.",
  },
  chirpFlood: {
    id: "chirpFlood", name: "Chirp Flood", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A stack of tiny answers nobody requested.",
  },
  talkOver: {
    id: "talkOver", name: "Talk Over", type: "viral", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Cuts in until the other joke loses the floor.",
  },
  reedRattle: {
    id: "reedRattle", name: "Reed Rattle", type: "chaos", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Shakes the hedge until every bird joins in.",
  },
  encoreLoop: {
    id: "encoreLoop", name: "Encore Loop", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 1, target: "self" },
    lore: "One more chorus. Then one more after that.",
  },
  rantBurst: {
    id: "rantBurst", name: "Rant Burst", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The hedge stops asking questions and starts making announcements.",
  },
  nestRiot: {
    id: "nestRiot", name: "Nest Riot", type: "chaos", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 1,
    lore: "The hedge calls a vote. Nobody counts. Everybody yells.",
  },
  quietRead: {
    id: "quietRead", name: "Quiet Read", type: "signal", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Skims in total silence. Still leaves a thumbprint on the page.",
  },
  tabHoard: {
    id: "tabHoard", name: "Tab Hoard", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Keeps forty tabs open as a quilt.",
  },
  sideGlance: {
    id: "sideGlance", name: "Side Glance", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Looks once. They suddenly feel underdressed.",
  },
  footnote: {
    id: "footnote", name: "Footnote", type: "cope", power: 60, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "A small aside in nine-point type. It broke something anyway.",
  },
  novelDump: {
    id: "novelDump", name: "Novel Dump", type: "signal", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The lurker posts. Bring a chair.",
  },
  marginBite: {
    id: "marginBite", name: "Margin Bite", type: "cope", power: 65, acc: 100, pp: 15,
    kind: "strike", crit: 0,
    fx: { kind: "drain", pct: 0.4 },
    lore: "Takes a sip of courage from the footnotes.",
  },
  replySnap: {
    id: "replySnap", name: "Reply Snap", type: "signal", power: 50, acc: 100, pp: 25,
    kind: "strike", crit: 1,
    lore: "Short, first, and already screenshotted by someone kinder.",
  },
  quotePin: {
    id: "quotePin", name: "Quote Pin", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Pins their last line where everyone can see it.",
  },
  dunkSplash: {
    id: "dunkSplash", name: "Dunk Splash", type: "chaos", power: 45, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A wet L lands across the lily pads.",
  },
  lPlus: {
    id: "lPlus", name: "L Plus", type: "chaos", power: 70, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Adds an L, then a ratio, then a little wave.",
  },
  ratioBomb: {
    id: "ratioBomb", name: "Ratio Bomb", type: "signal", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The replies arrive in a single clean stack.",
  },
  threadFlood: {
    id: "threadFlood", name: "Thread Flood", type: "chaos", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "The pond fills with quote tweets. The lily pads go under.",
  },
  flickDown: {
    id: "flickDown", name: "Flick Down", type: "chaos", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A tiny claw flicks to the next disaster.",
  },
  keepReading: {
    id: "keepReading", name: "Keep Reading", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "One more item. Their feet forget the exit.",
  },
  tabSip: {
    id: "tabSip", name: "Tab Sip", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.35 },
    lore: "A cold leftover headline, sipped for comfort.",
  },
  doomFlick: {
    id: "doomFlick", name: "Doom Flick", type: "chaos", power: 70, acc: 90, pp: 12,
    kind: "strike", crit: 0,
    fx: { kind: "recoil", pct: 0.1 },
    lore: "Finds the worst page on purpose. Feels every line of it out loud.",
  },
  bingeSwipe: {
    id: "bingeSwipe", name: "Binge Swipe", type: "chaos", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A whole night of news in one long claw.",
  },
  nightFeed: {
    id: "nightFeed", name: "Night Feed", type: "chaos", power: 85, acc: 90, pp: 8,
    kind: "strike", crit: 0,
    fx: { kind: "recoil", pct: 0.1 },
    lore: "The last item is never the last item.",
  },
  politeNod: {
    id: "politeNod", name: "Polite Nod", type: "flex", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Agrees that the room is, in fact, fine.",
  },
  kettleSit: {
    id: "kettleSit", name: "Kettle Sit", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Settles deeper into the warm tin. Nobody is getting it out of there.",
  },
  fineThanks: {
    id: "fineThanks", name: "Fine Thanks", type: "cope", power: 40, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Thanks them so politely they lose the joke.",
  },
  steamHuff: {
    id: "steamHuff", name: "Steam Huff", type: "cope", power: 65, acc: 95, pp: 15,
    kind: "strike", crit: 0,
    lore: "A polite cloud of kettle weather. The steam does the shouting.",
  },
  stillFine: {
    id: "stillFine", name: "Still Fine", type: "flex", power: 75, acc: 100, pp: 12,
    kind: "strike", crit: 0,
    fx: { kind: "flinch", chance: 0.15 },
    lore: "The helmeted nod that ends the meeting.",
  },
  hearthRoar: {
    id: "hearthRoar", name: "Hearth Roar", type: "flex", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The kettle finally admits it is a kiln.",
  },
  blankStare: {
    id: "blankStare", name: "Blank Stare", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "One rehearsed look. They forget their line.",
  },
  scriptLine: {
    id: "scriptLine", name: "Script Line", type: "signal", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Delivers the only sentence in the booklet.",
  },
  idleLoop: {
    id: "idleLoop", name: "Idle Loop", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Nods in place until the HP bar agrees.",
  },
  copyPaste: {
    id: "copyPaste", name: "Copy Paste", type: "viral", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Repeats the same line with fresh confidence.",
  },
  faqDump: {
    id: "faqDump", name: "FAQ Dump", type: "signal", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Opens the manual at the loudest page.",
  },
  canonDrop: {
    id: "canonDrop", name: "Canon Drop", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Cites itself. The lane accepts the citation.",
  },
  latchClick: {
    id: "latchClick", name: "Latch Click", type: "chaos", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A tiny latch snaps shut on the wrong side.",
  },
  guestList: {
    id: "guestList", name: "Guest List", type: "signal", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Checks a name that was never written down.",
  },
  fenceHop: {
    id: "fenceHop", name: "Fence Hop", type: "chaos", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Clears the slats and lands in your lane.",
  },
  keepOut: {
    id: "keepOut", name: "Keep Out", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Nails a polite sign to a very firm post.",
  },
  barSlam: {
    id: "barSlam", name: "Bar Slam", type: "chaos", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The bar drops. The conversation ends.",
  },
  yardShut: {
    id: "yardShut", name: "Yard Shut", type: "signal", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Closes the yard around whoever was visiting.",
  },
  softBuff: {
    id: "softBuff", name: "Soft Buff", type: "flex", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Buffs so gently it counts as an apology. It is still a hit.",
  },
  highShine: {
    id: "highShine", name: "High Shine", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Waxes its own courage until it gleams.",
  },
  polishPuff: {
    id: "polishPuff", name: "Polish Puff", type: "flex", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A cloud of compliments with grit in it.",
  },
  yesAnd: {
    id: "yesAnd", name: "Yes And", type: "flex", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Agrees so hard it mends a little HP.",
  },
  fullGlaze: {
    id: "fullGlaze", name: "Full Glaze", type: "flex", power: 80, acc: 95, pp: 10,
    kind: "strike", crit: 0,
    lore: "Coats the lane until the threat looks expensive.",
  },
  compliment: {
    id: "compliment", name: "Compliment", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    fx: { kind: "flinch", chance: 0.15 },
    lore: "Praises them so hard they miss a turn.",
  },
  hatTip: {
    id: "hatTip", name: "Hat Tip", type: "viral", power: 50, acc: 100, pp: 25,
    kind: "strike", crit: 1,
    lore: "The brim does the talking. Whatever is under it takes no questions.",
  },
  fibSpin: {
    id: "fibSpin", name: "Fib Spin", type: "chaos", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "A story so tall their guard slips.",
  },
  tallTale: {
    id: "tallTale", name: "Tall Tale", type: "chaos", power: 55, acc: 95, pp: 20,
    kind: "strike", crit: 0,
    lore: "The hat grows two sizes mid-sentence.",
  },
  peakCap: {
    id: "peakCap", name: "Peak Cap", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 1, target: "self" },
    lore: "Tilts the brim and leaves first.",
  },
  truthKick: {
    id: "truthKick", name: "Truth Kick", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A stamped hoof. No footnotes.",
  },
  stampede: {
    id: "stampede", name: "Stampede", type: "viral", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The record arrives with the whole herd.",
  },
  turfTap: {
    id: "turfTap", name: "Turf Tap", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A polite kick toward the actual ground.",
  },
  goOutside: {
    id: "goOutside", name: "Go Outside", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Points at the door until they slow down.",
  },
  seedToss: {
    id: "seedToss", name: "Seed Toss", type: "viral", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Throws a handful of tomorrow at their boots.",
  },
  sitLawn: {
    id: "sitLawn", name: "Sit Lawn", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Sits down. The lane has to go around.",
  },
  lawnSweep: {
    id: "lawnSweep", name: "Lawn Sweep", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "One wide rake. The argument comes up with the leaves.",
  },
  parkDecree: {
    id: "parkDecree", name: "Park Decree", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The park posts hours. The thread closes.",
  },
  simmer: {
    id: "simmer", name: "Simmer", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A low boil under a very small lid.",
  },
  steamPop: {
    id: "steamPop", name: "Steam Pop", type: "chaos", power: 55, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The lid hops. The point lands.",
  },
  holdIt: {
    id: "holdIt", name: "Hold It", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Keeps the argument inside the pot. The pot is not enjoying this.",
  },
  maldSip: {
    id: "maldSip", name: "Mald Sip", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Drinks the leftover steam and sits back down.",
  },
  fullSeethe: {
    id: "fullSeethe", name: "Full Seethe", type: "cope", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The pot finally uses its outdoor voice.",
  },
  lidBlow: {
    id: "lidBlow", name: "Lid Blow", type: "chaos", power: 85, acc: 90, pp: 8,
    kind: "strike", crit: 0,
    fx: { kind: "recoil", pct: 0.1 },
    lore: "The lid leaves. So does a little composure.",
  },
  roseLens: {
    id: "roseLens", name: "Rose Lens", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Through the lens, their guard is a suggestion. A sweet one.",
  },
  maybeSo: {
    id: "maybeSo", name: "Maybe So", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A hopeful tap. Counts anyway. Most hopeful things do not.",
  },
  daydream: {
    id: "daydream", name: "Daydream", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.35 },
    lore: "Sits in a nicer version of this puddle.",
  },
  plotArmor: {
    id: "plotArmor", name: "Plot Armor", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "The script says they cannot lose this scene.",
  },
  canonWish: {
    id: "canonWish", name: "Canon Wish", type: "signal", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Cites a page that is still being written.",
  },
  mainCharacter: {
    id: "mainCharacter", name: "Main Character", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Walks in as if the lane was built around them.",
  },
  vibeCheck: {
    id: "vibeCheck", name: "Vibe Check", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Rates the room. Then rates itself higher.",
  },
  moodWave: {
    id: "moodWave", name: "Mood Wave", type: "viral", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Splashes back whatever the lane was already feeling. Louder.",
  },
  nodOff: {
    id: "nodOff", name: "Nod Off", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "A slow nod that makes their swing look optional.",
  },
  bouncePass: {
    id: "bouncePass", name: "Bounce Pass", type: "viral", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Hops the joke over and expects it back louder.",
  },
  vibeSweep: {
    id: "vibeSweep", name: "Vibe Sweep", type: "viral", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The shades flash. The whole puddle agrees.",
  },
  crowdSurge: {
    id: "crowdSurge", name: "Crowd Surge", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The whole room leans in at once. The floor files a complaint.",
  },
  unsent: {
    id: "unsent", name: "Unsent", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Folds the note again. Safer in the pocket.",
  },
  scratchNote: {
    id: "scratchNote", name: "Scratch Note", type: "signal", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A pencil tap that still counts as a draft.",
  },
  saveDraft: {
    id: "saveDraft", name: "Save Draft", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Closes the page. The crease holds. The draft still does not send.",
  },
  marginTap: {
    id: "marginTap", name: "Margin Tap", type: "cope", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Knocks on the sidebar until someone looks.",
  },
  threadPost: {
    id: "threadPost", name: "Thread Post", type: "signal", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Hits send. The lane has to read it.",
  },
  replyStorm: {
    id: "replyStorm", name: "Reply Storm", type: "cope", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Six drafts land in the order they were regretted.",
  },
  peekAhead: {
    id: "peekAhead", name: "Peek Ahead", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Tells them how the guard scene ends.",
  },
  leakDrop: {
    id: "leakDrop", name: "Leak Drop", type: "chaos", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A page falls out face up, three chapters ahead of where you were.",
  },
  hushBark: {
    id: "hushBark", name: "Hush Bark", type: "signal", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A whisper with teeth. Quiet enough to be polite, loud enough to spoil it.",
  },
  endingHint: {
    id: "endingHint", name: "Ending Hint", type: "chaos", power: 0, acc: 100, pp: 15,
    kind: "hex", crit: 0,
    fx: { kind: "surehit" },
    lore: "The last page is already in their pocket.",
  },
  plotDump: {
    id: "plotDump", name: "Plot Dump", type: "chaos", power: 85, acc: 90, pp: 8,
    kind: "strike", crit: 0,
    lore: "Posts the whole ending in one splash.",
  },
  spoilerHowl: {
    id: "spoilerHowl", name: "Spoiler Howl", type: "signal", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Names the twist before the credits.",
  },
  takeANumber: {
    id: "takeANumber", name: "Take a Number", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Holds a ticket. The lane has to wait.",
  },
  waitDrip: {
    id: "waitDrip", name: "Wait Drip", type: "liquid", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A patient splash. Waits its turn. Fills the boot anyway.",
  },
  shuffleUp: {
    id: "shuffleUp", name: "Shuffle Up", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Moves one step. The line thinks it progressed.",
  },
  holdPlace: {
    id: "holdPlace", name: "Hold Place", type: "liquid", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Saves their spot. They cannot leave it.",
  },
  counterCall: {
    id: "counterCall", name: "Counter Call", type: "liquid", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The window opens. Everyone hears their number.",
  },
  rushLane: {
    id: "rushLane", name: "Rush Lane", type: "cope", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Cuts the queue with a polite apology.",
  },
  paperClip: {
    id: "paperClip", name: "Paper Clip", type: "signal", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Pins the joke to the page so it cannot claim it was a joke.",
  },
  inkStamp: {
    id: "inkStamp", name: "Ink Stamp", type: "flex", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A wet date lands on the forehead. It does not come off by Tuesday.",
  },
  fileAway: {
    id: "fileAway", name: "File Away", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Puts the argument in a folder and sits on it.",
  },
  showProof: {
    id: "showProof", name: "Show Proof", type: "flex", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Holds up the slip. Their swing looks smaller.",
  },
  auditSlam: {
    id: "auditSlam", name: "Audit Slam", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The ledger closes on their toes.",
  },
  ledgerBeam: {
    id: "ledgerBeam", name: "Ledger Beam", type: "signal", power: 85, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "A column of dates. No room to edit.",
  },
  snipCut: {
    id: "snipCut", name: "Snip Cut", type: "viral", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Keeps two seconds and throws the rest.",
  },
  contextToss: {
    id: "contextToss", name: "Context Toss", type: "chaos", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Throws the setup away. The punchline flies alone.",
  },
  outOfFrame: {
    id: "outOfFrame", name: "Out of Frame", type: "chaos", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Crops them. They have to catch up to the cut.",
  },
  loopClip: {
    id: "loopClip", name: "Loop Clip", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 2, target: "self" },
    lore: "Plays itself again. Faster the second time.",
  },
  viralCut: {
    id: "viralCut", name: "Viral Cut", type: "viral", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The two-second version hits the whole route.",
  },
  montage: {
    id: "montage", name: "Montage", type: "chaos", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Six cuts, no breath, one landing.",
  },
  brimTip: {
    id: "brimTip", name: "Brim Tip", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Tips the toque. Suddenly it looks taller.",
  },
  snowPuff: {
    id: "snowPuff", name: "Snow Puff", type: "cope", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A polite flurry, pompom-launched. Still lands cold down the collar.",
  },
  earFlap: {
    id: "earFlap", name: "Ear Flap", type: "flex", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The flap slaps. Winter has opinions.",
  },
  stayWarm: {
    id: "stayWarm", name: "Stay Warm", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Pulls the hat down. The ears recover.",
  },
  tuqueSlam: {
    id: "tuqueSlam", name: "Tuque Slam", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Lands hat first. There was never a head to follow.",
  },
  blizzardNod: {
    id: "blizzardNod", name: "Blizzard Nod", type: "cope", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A nod that comes with weather.",
  },
  curdToss: {
    id: "curdToss", name: "Curd Toss", type: "liquid", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Squeaky ammo, room temperature. It still counts.",
  },
  gravyDip: {
    id: "gravyDip", name: "Gravy Dip", type: "liquid", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "A warm splash with staying power. The boots are in it now.",
  },
  cheesePull: {
    id: "cheesePull", name: "Cheese Pull", type: "flex", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Stretches their guard until it snaps.",
  },
  napkinWrap: {
    id: "napkinWrap", name: "Napkin Wrap", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Folds a paper cape. Lunch is protected.",
  },
  gravyWave: {
    id: "gravyWave", name: "Gravy Wave", type: "liquid", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The boat tips. The lane is gravy.",
  },
  platterSlam: {
    id: "platterSlam", name: "Platter Slam", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Sets the platter down like a verdict.",
  },
  leftOnRead: {
    id: "leftOnRead", name: "Left on Read", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Opens the note. Does not answer.",
  },
  fadeTap: {
    id: "fadeTap", name: "Fade Tap", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A tap that is already leaving.",
  },
  seenOnce: {
    id: "seenOnce", name: "Seen Once", type: "signal", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The grey tick hits harder than a reply.",
  },
  muteChat: {
    id: "muteChat", name: "Mute Chat", type: "cope", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Archives the thread. Nothing in there was going to improve.",
  },
  unreadBomb: {
    id: "unreadBomb", name: "Unread Bomb", type: "signal", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Nine days of unread, delivered as one stack. Receipt enclosed.",
  },
  vanishHowl: {
    id: "vanishHowl", name: "Vanish Howl", type: "cope", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Leaves so loudly it still counts as a hit.",
  },
  crashOut: {
    id: "crashOut", name: "Crash Out", type: "chaos", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The leash slips. The argument starts.",
  },
  rantBite: {
    id: "rantBite", name: "Rant Bite", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A short bark that quotes the last post.",
  },
  redThread: {
    id: "redThread", name: "Red Thread", type: "chaos", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Pulls the thread tighter. The swing grows.",
  },
  takeFive: {
    id: "takeFive", name: "Take Five", type: "viral", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Steps out. Comes back louder.",
  },
  fullCrash: {
    id: "fullCrash", name: "Full Crash", type: "chaos", power: 85, acc: 90, pp: 8,
    kind: "strike", crit: 0,
    lore: "Reply nine hundred. The leash wins. The floor takes the rest.",
  },
  rantStorm: {
    id: "rantStorm", name: "Rant Storm", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every bark it swallowed this week comes back up. Louder.",
  },
  dropSauce: {
    id: "dropSauce", name: "Drop Sauce", type: "flex", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Uncorks a little. The lane leans in.",
  },
  sourceLink: {
    id: "sourceLink", name: "Source Link", type: "chaos", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "def", stages: -1, target: "foe" },
    lore: "Points at a recipe they cannot open.",
  },
  tasteTest: {
    id: "tasteTest", name: "Taste Test", type: "flex", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A dab on the tongue. Still a hit.",
  },
  bottleCap: {
    id: "bottleCap", name: "Bottle Cap", type: "chaos", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 2, target: "self" },
    lore: "Pops the lid. Faster on the second pour.",
  },
  sauceFlood: {
    id: "sauceFlood", name: "Sauce Flood", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The bottle forgets the word drizzle.",
  },
  recipeSlam: {
    id: "recipeSlam", name: "Recipe Slam", type: "chaos", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Drops the whole card. No measurements.",
  },
  pingPing: {
    id: "pingPing", name: "Ping Ping", type: "viral", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A tiny badge. It still finds the ear.",
  },
  badgePop: {
    id: "badgePop", name: "Badge Pop", type: "signal", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "The unread count ticks up. So does the swing.",
  },
  muteSwipe: {
    id: "muteSwipe", name: "Mute Swipe", type: "signal", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Clears one alert by hitting them with it.",
  },
  quietHours: {
    id: "quietHours", name: "Quiet Hours", type: "viral", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Silences the badge. The pocket is a bunker.",
  },
  pingStorm: {
    id: "pingStorm", name: "Ping Storm", type: "viral", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every badge fires. The lane has no Do Not Disturb.",
  },
  alertBlast: {
    id: "alertBlast", name: "Alert Blast", type: "signal", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "One banner. Full volume. No preview.",
  },
  slayPose: {
    id: "slayPose", name: "Slay Pose", type: "viral", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Strikes a pose. The lane applauds anyway.",
  },
  lookBook: {
    id: "lookBook", name: "Look Book", type: "flex", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Flips a page of fits. One of them hits.",
  },
  walkOff: {
    id: "walkOff", name: "Walk Off", type: "viral", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: 1, target: "self" },
    lore: "Leaves the frame. Comes back faster.",
  },
  rimLight: {
    id: "rimLight", name: "Rim Light", type: "flex", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Lights the edge. Harder to unsee.",
  },
  slayStorm: {
    id: "slayStorm", name: "Slay Storm", type: "viral", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every pose at once. The runway is a weather event.",
  },
  coutureSlam: {
    id: "coutureSlam", name: "Couture Slam", type: "flex", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Drops the whole look. No accessories left.",
  },
  letCook: {
    id: "letCook", name: "Let Cook", type: "flex", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Gives it a minute. The pan still hits.",
  },
  spiceToss: {
    id: "spiceToss", name: "Spice Toss", type: "chaos", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A pinch from across the lane. Eyes water.",
  },
  lowHeat: {
    id: "lowHeat", name: "Low Heat", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Turns it down. Everything thickens, the swing included.",
  },
  lidOn: {
    id: "lidOn", name: "Lid On", type: "chaos", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Covers the pot. Steam does the rest.",
  },
  cookOff: {
    id: "cookOff", name: "Cook Off", type: "flex", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The kitchen forgets the word tasteful.",
  },
  kitchenFire: {
    id: "kitchenFire", name: "Kitchen Fire", type: "chaos", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The alarm is a suggestion. The pan is not.",
  },
  midTake: {
    id: "midTake", name: "Mid Take", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A take so carefully average that nobody can duck it.",
  },
  nuanceDump: {
    id: "nuanceDump", name: "Nuance Dump", type: "signal", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Both sides, a thread, no conclusion.",
  },
  bothSides: {
    id: "bothSides", name: "Both Sides", type: "cope", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Agrees with everyone. Nobody swings clean.",
  },
  stayMid: {
    id: "stayMid", name: "Stay Mid", type: "signal", power: 0, acc: 100, pp: 15,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 2, target: "self" },
    lore: "Refuses to pick a lane. Stands in both. Hard to aim at.",
  },
  midwave: {
    id: "midwave", name: "Midwave", type: "cope", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The average take becomes a weather system.",
  },
  centristSlam: {
    id: "centristSlam", name: "Centrist Slam", type: "signal", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Reads the both-sides essay aloud. All of it. From the top.",
  },
  lockIn: {
    id: "lockIn", name: "Lock In", type: "signal", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Clicks the padlock. The grind starts.",
  },
  screenTime: {
    id: "screenTime", name: "Screen Time", type: "flex", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "The timer hits. The phone still swings.",
  },
  timeout: {
    id: "timeout", name: "Timeout", type: "signal", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "App limit reached. Feet get slower.",
  },
  focusMode: {
    id: "focusMode", name: "Focus Mode", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Everything else goes grey. What is left swings twice as hard.",
  },
  lockStorm: {
    id: "lockStorm", name: "Lock Storm", type: "signal", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every app hits its limit at once. The shackle kept all the keys.",
  },
  overtime: {
    id: "overtime", name: "Overtime", type: "flex", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The lock stays shut. The shift does not.",
  },
  lateOpen: {
    id: "lateOpen", name: "Late Open", type: "chaos", power: 50, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "The door is still unlocked. The bell hits.",
  },
  slushToss: {
    id: "slushToss", name: "Slush Toss", type: "liquid", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A cup from the machine. Blue, somehow.",
  },
  lottoScratch: {
    id: "lottoScratch", name: "Lotto Scratch", type: "chaos", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Almost a winner. The next ticket swings harder.",
  },
  afterHours: {
    id: "afterHours", name: "After Hours", type: "liquid", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Closes the till. The slush still pours.",
  },
  slushFlood: {
    id: "slushFlood", name: "Slush Flood", type: "liquid", power: 80, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Blue reaches the ankles. It was never a question of cup size.",
  },
  cornerSlam: {
    id: "cornerSlam", name: "Corner Slam", type: "chaos", power: 75, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "Puts the entire dépanneur counter down on the argument.",
  },
  politeTap: {
    id: "politeTap", name: "Polite Tap", type: "flex", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Excuse me. The tap has already happened.",
  },
  ehEcho: {
    id: "ehEcho", name: "Eh Echo", type: "viral", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "One eh bounces back with a whole conversation.",
  },
  sorryBud: {
    id: "sorryBud", name: "Sorry Bud", type: "cope", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Apologizes so hard the other swing loses steam.",
  },
  budCoat: {
    id: "budCoat", name: "Bud Coat", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 1, target: "self" },
    lore: "Tucks into a shell. Spring can wait.",
  },
  canopyClap: {
    id: "canopyClap", name: "Canopy Clap", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every branch offers a firm little handshake.",
  },
  chorusEh: {
    id: "chorusEh", name: "Chorus Eh", type: "viral", power: 80, acc: 90, pp: 10,
    kind: "strike", crit: 0,
    lore: "The whole canopy asks if you caught that.",
  },
  pillowBop: {
    id: "pillowBop", name: "Pillow Bop", type: "cope", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A soft launch with one very firm corner.",
  },
  yawnPuff: {
    id: "yawnPuff", name: "Yawn Puff", type: "chaos", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "The yawn left before the thought arrived.",
  },
  snoozeButton: {
    id: "snoozeButton", name: "Snooze Button", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Borrows five minutes from a very patient alarm.",
  },
  blanketFort: {
    id: "blanketFort", name: "Blanket Fort", type: "cope", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 1, target: "self" },
    lore: "The duvet is structural. Please knock.",
  },
  napquake: {
    id: "napquake", name: "Napquake", type: "cope", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Rolls over. The route rolls with it.",
  },
  dreamSpill: {
    id: "dreamSpill", name: "Dream Spill", type: "chaos", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The pillow leaks an entire unfinished dream.",
  },
  coneTap: {
    id: "coneTap", name: "Cone Tap", type: "chaos", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "An orange warning that travels by forehead.",
  },
  laneToss: {
    id: "laneToss", name: "Lane Toss", type: "flex", power: 45, acc: 100, pp: 20,
    kind: "strike", crit: 0,
    lore: "Moves the lane. You were standing in it.",
  },
  reroute: {
    id: "reroute", name: "Reroute", type: "chaos", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "The shortcut has acquired three extra corners.",
  },
  workZone: {
    id: "workZone", name: "Work Zone", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Puts the vest on. Now it means business.",
  },
  detourRush: {
    id: "detourRush", name: "Detour Rush", type: "chaos", power: 80, acc: 90, pp: 12,
    kind: "strike", crit: 0,
    lore: "All six feet take a different shortcut.",
  },
  laneClosure: {
    id: "laneClosure", name: "Lane Closure", type: "flex", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A row of warning lights becomes one solid argument.",
  },
  sapSlap: {
    id: "sapSlap", name: "Sap Slap", type: "liquid", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A sticky paw with impeccable local sourcing.",
  },
  mapleDrip: {
    id: "mapleDrip", name: "Maple Drip", type: "liquid", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Straight from the bucket. Boots sold separately.",
  },
  stickyPaws: {
    id: "stickyPaws", name: "Sticky Paws", type: "viral", power: 0, acc: 95, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "The floor has subscribed to your footsteps.",
  },
  sugarRest: {
    id: "sugarRest", name: "Sugar Rest", type: "liquid", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "A quiet minute by the sugar-shack kettle.",
  },
  syrupSurge: {
    id: "syrupSurge", name: "Syrup Surge", type: "liquid", power: 80, acc: 90, pp: 12,
    kind: "strike", crit: 0,
    lore: "The tap opens. The whole lane becomes breakfast.",
  },
  sweetStamp: {
    id: "sweetStamp", name: "Sweet Stamp", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Seals the deal with a maple-shaped footprint.",
  },
  bufferBite: {
    id: "bufferBite", name: "Buffer Bite", type: "signal", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "The teeth arrive before the loading ring finishes.",
  },
  lagSpike: {
    id: "lagSpike", name: "Lag Spike", type: "signal", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A missing moment lands point-first.",
  },
  pleaseWait: {
    id: "pleaseWait", name: "Please Wait", type: "cope", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "The progress bar drains the will to swing.",
  },
  cacheNap: {
    id: "cacheNap", name: "Cache Nap", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Closes a few tabs and finds room to breathe.",
  },
  packetBurst: {
    id: "packetBurst", name: "Packet Burst", type: "signal", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Fourteen hops queued behind one tired router. All of them land now.",
  },
  loadingLoop: {
    id: "loadingLoop", name: "Loading Loop", type: "cope", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Throws the spinning ring. It is your problem now.",
  },
  // Craft R10: five original lines, six moves per line.
  honkTap: {
    id: "honkTap", name: "Honk Tap", type: "liquid", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A small bill with a very large opinion.",
  },
  voiceNote: {
    id: "voiceNote", name: "Voice Note", type: "signal", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Sends a pond echo nobody asked to download.",
  },
  micCheck: {
    id: "micCheck", name: "Mic Check", type: "signal", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: 1, target: "self" },
    lore: "Tests the microphone. The reeds flinch politely.",
  },
  muteThread: {
    id: "muteThread", name: "Mute Thread", type: "cope", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "Silences the thread long enough to think.",
  },
  honkCast: {
    id: "honkCast", name: "Honk Cast", type: "signal", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Broadcasts the minutes across the entire marsh.",
  },
  pondOvation: {
    id: "pondOvation", name: "Pond Ovation", type: "liquid", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Every puddle applauds. Nobody asked for the encore. It gets one.",
  },
  chillNip: {
    id: "chillNip", name: "Chill Nip", type: "cope", power: 42, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A cool little reminder to unclench.",
  },
  frostSip: {
    id: "frostSip", name: "Frost Sip", type: "liquid", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Flicks the last cold drop from its lid.",
  },
  touchSnow: {
    id: "touchSnow", name: "Touch Snow", type: "cope", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "spd", stages: -1, target: "foe" },
    lore: "Invites the hurry to sit in a snowbank.",
  },
  teaBreak: {
    id: "teaBreak", name: "Tea Break", type: "liquid", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "A warm sip inside a very cold coat.",
  },
  chillFront: {
    id: "chillFront", name: "Chill Front", type: "cope", power: 76, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A weather system with no plans this weekend.",
  },
  icebreaker: {
    id: "icebreaker", name: "Icebreaker", type: "liquid", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Breaks the ice and the awkward silence.",
  },
  crumbPeck: {
    id: "crumbPeck", name: "Crumb Peck", type: "flex", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "One tiny peck toward a very large lunch.",
  },
  bagelSkip: {
    id: "bagelSkip", name: "Bagel Skip", type: "viral", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Skips a sesame ring across the timeline.",
  },
  proofRest: {
    id: "proofRest", name: "Proof Rest", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Lets the dough and the shoulders rise.",
  },
  seededCoat: {
    id: "seededCoat", name: "Seeded Coat", type: "flex", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 1, target: "self" },
    lore: "Buttons a coat of stubborn sesame seeds.",
  },
  breadQuake: {
    id: "breadQuake", name: "Bread Quake", type: "flex", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Crust first, seeds second, apology never.",
  },
  bakerDozen: {
    id: "bakerDozen", name: "Baker Dozen", type: "viral", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "Thirteen rings. One extremely full inbox.",
  },
  puckTap: {
    id: "puckTap", name: "Puck Tap", type: "flex", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "A polite tap before the faceoff gets personal.",
  },
  rinkRattle: {
    id: "rinkRattle", name: "Rink Rattle", type: "chaos", power: 42, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Banks a loose puck off every opinion.",
  },
  benchBreath: {
    id: "benchBreath", name: "Bench Breath", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Takes a short shift on the quiet end of the bench.",
  },
  chirpCheck: {
    id: "chirpCheck", name: "Chirp Check", type: "chaos", power: 0, acc: 100, pp: 20,
    kind: "hex", crit: 0,
    fx: { kind: "stat", stat: "atk", stages: -1, target: "foe" },
    lore: "A tiny chirp takes the swagger out of the swing.",
  },
  clapShot: {
    id: "clapShot", name: "Clap Shot", type: "flex", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "A clean reply straight off the blue line.",
  },
  boardCrash: {
    id: "boardCrash", name: "Board Crash", type: "chaos", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The boards carry the argument around the rink.",
  },
  tabTug: {
    id: "tabTug", name: "Tab Tug", type: "signal", power: 45, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Pulls the tab you were definitely still reading.",
  },
  linkFling: {
    id: "linkFling", name: "Link Fling", type: "chaos", power: 40, acc: 100, pp: 25,
    kind: "strike", crit: 0,
    lore: "Sends a useful link with fourteen more inside.",
  },
  closeTabs: {
    id: "closeTabs", name: "Close Tabs", type: "cope", power: 0, acc: 100, pp: 10,
    kind: "guard", crit: 0,
    fx: { kind: "heal", pct: 0.3 },
    lore: "Finds a little peace between two closed pages.",
  },
  pinTab: {
    id: "pinTab", name: "Pin Tab", type: "signal", power: 0, acc: 100, pp: 20,
    kind: "guard", crit: 0,
    fx: { kind: "stat", stat: "def", stages: 1, target: "self" },
    lore: "Pins the important thought where it cannot drift.",
  },
  tabCascade: {
    id: "tabCascade", name: "Tab Cascade", type: "signal", power: 78, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "One of these tabs is playing audio. Good luck finding it.",
  },
  bookmarkFall: {
    id: "bookmarkFall", name: "Bookmark Fall", type: "chaos", power: 75, acc: 95, pp: 12,
    kind: "strike", crit: 0,
    lore: "The reading list finally comes down off the shelf.",
  },
  flailPost: {
    id: "flailPost",
    name: "Flail Post",
    type: "chaos",
    power: 40,
    acc: 100,
    pp: 1,
    kind: "strike",
    crit: 0,
    fx: { kind: "recoil", pct: 0.2 },
    lore: "No PP. Post anyway. The timeline will invoice you.",
  },
};

// Frontier kit (BURN / HODL / RUG, volleys and quick strikes), the Undernet kit (AI / MOON and
// the first condition moves), the Sidechain kit (STAKE / REKT) and the level-up toolkit join the same table.
for (const [id, def] of [...Object.entries(FRONTIER_MOVES), ...Object.entries(UNDERNET_MOVES), ...Object.entries(SIDECHAIN_MOVES), ...Object.entries(TOOLKIT_MOVES)]) {
  if (Object.prototype.hasOwnProperty.call(MOVES, id)) throw new Error(`duplicate move ${id}`);
  MOVES[id] = def;
}

export function moveById(id: string): MoveDef {
  const m = peekMove(id);
  if (!m) throw new Error(`move ${id}`);
  return m;
}

export function peekMove(id: string): MoveDef | null {
  return Object.prototype.hasOwnProperty.call(MOVES, id) ? MOVES[id]! : null;
}

/** Thirteen bitmap cells fit the move-detail well without hiding PP or accuracy. */
export function moveEffectLabel(move: MoveDef): string {
  const fx = move.fx;
  if (!fx && (move.priority ?? 0) > 0) return "STRIKES FIRST";
  if (!fx) return move.crit > 0 ? `CRIT ${Math.round(6 + move.crit * 8)}%` : "DIRECT HIT";
  if (fx.kind === "multi") return `HITS ${fx.min}-${fx.max}`;
  if (fx.kind === "stat") return `${fx.target === "self" ? "SELF" : "FOE"} ${fx.stat.toUpperCase()} ${fx.stages > 0 ? "+" : ""}${fx.stages}`;
  if (fx.kind === "heal") return `HEAL ${Math.round(fx.pct * 100)}%`;
  if (fx.kind === "drain") return `DRAIN ${Math.round(fx.pct * 100)}%`;
  if (fx.kind === "recoil") return `RECOIL ${Math.round(fx.pct * 100)}%`;
  if (fx.kind === "flinch") return `FLINCH ${Math.round(fx.chance * 100)}%`;
  if (fx.kind === "status") return fx.chance >= 1 ? STATUS_META[fx.status].name.toUpperCase()
    : `${STATUS_META[fx.status].chip} ${Math.round(fx.chance * 100)}%`;
  if (fx.kind === "stats") return statPairs(fx).map(([k, v]) => `${k.toUpperCase()}${v > 0 ? "+" : ""}${v}`).join(" ");
  if (fx.kind === "cure") return `CURE HEAL ${Math.round(fx.pct * 100)}%`;
  return "NEXT HIT 100%";
}

/** The stat changes a two-stat boost makes, in ATK, DEF, SPD order. */
export function statPairs(fx: { atk?: number; def?: number; spd?: number }): ["atk" | "def" | "spd", number][] {
  return (["atk", "def", "spd"] as const).filter((k) => fx[k]).map((k) => [k, fx[k]!]);
}
